import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Avatar from '@atlaskit/avatar/avatar';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import { Stack } from '@atlaskit/primitives/compiled/stack';

import {
	ANALYTICS_SOURCE,
	RELATED_PEOPLE_ROW_ACTION_SUBJECT_ID,
	RELATED_PEOPLE_SHOW_MORE_ACTION_SUBJECT_ID,
} from '../../analytics.ts';
import type { RankedPagePerson } from '../../utils/rankPagePeople.ts';
import { useExpandedState } from '../connections-by-line-section/ConnectionsByLineSectionShell.tsx';
import { PersonRow } from '../pressable-group/PersonRow.tsx';
import { PressableGroup } from '../pressable-group/PressableGroup.tsx';
import { SectionHeader } from '../pressable-group/SectionHeader.tsx';
import { ShowMoreRow } from '../pressable-group/ShowMoreRow.tsx';

import { SectionEmptyState } from './SectionEmptyState.tsx';
import type { SectionExpansionProps } from './useSectionExpansion.ts';

/**
 * How many people the collapsed section lists. The rest are one press away, up to the
 * `MAX_LISTED_PAGE_PEOPLE` `useConnectionSectionsData` asks `usePagePeople` for.
 */
export const COLLAPSED_PAGE_PEOPLE = 3;

const i18n = defineMessages({
	heading: {
		id: 'byline-connections.preview-panel.section.related-people',
		defaultMessage: 'Related people',
		description:
			'Heading of the Teamwork Graph section listing the people connected to this page, such as the people who created or contributed to it. The section shows the number of people in a badge beside the heading.',
	},
	empty: {
		id: 'byline-connections.preview-panel.section.related-people.empty',
		defaultMessage:
			'There are no contributors yet. Relationships are mapped automatically as your team works together on this page. Start a conversation or share this page to strengthen your team’s graph.',
		description:
			'Shown under the "Related people" heading of the Teamwork Graph panel, in place of the list of people, when the graph connects nobody to the current page yet.',
	},
});

const relationshipMessages = defineMessages({
	created: {
		id: 'byline-connections.preview-panel.person.relationship.created',
		defaultMessage: 'Creator of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person created the current page.',
	},
	contributed: {
		id: 'byline-connections.preview-panel.person.relationship.contributed',
		defaultMessage: 'Contributor of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person contributed to the current page.',
	},
	owns: {
		id: 'byline-connections.preview-panel.person.relationship.owns',
		defaultMessage: 'Owner of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person owns the current page.',
	},
	updated: {
		id: 'byline-connections.preview-panel.person.relationship.updated',
		defaultMessage: 'Editor of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person updated the current page.',
	},
	viewed: {
		id: 'byline-connections.preview-panel.person.relationship.viewed',
		defaultMessage: 'Viewer of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person viewed the current page.',
	},
	favorited: {
		id: 'byline-connections.preview-panel.person.relationship.favorited',
		defaultMessage: 'Favoriter of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person favorited the current page.',
	},
	liked: {
		id: 'byline-connections.preview-panel.person.relationship.liked',
		defaultMessage: 'Liker of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person liked the current page.',
	},
	'mentioned-in': {
		id: 'byline-connections.preview-panel.person.relationship.mentioned-in',
		defaultMessage: 'Mentionee of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person is mentioned in the current page.',
	},
	snapshotted: {
		id: 'byline-connections.preview-panel.person.relationship.snapshotted',
		defaultMessage: 'Snapshot creator of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person created a snapshot of the current page.',
	},
	watches: {
		id: 'byline-connections.preview-panel.person.relationship.watches',
		defaultMessage: 'Watcher of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person watches the current page.',
	},
	'shared-with': {
		id: 'byline-connections.preview-panel.person.relationship.shared-with',
		defaultMessage: 'Recipient of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person was given access through sharing of the current page.',
	},
});

const messageForRelationship = (relationship: keyof typeof relationshipMessages) =>
	relationshipMessages[relationship];

/**
 * The person's profile in the people directory. Never `encodeURIComponent` the account id — the
 * colon in `70121:ed05…` is legal in a path, and `%3A` gives a URL the route cannot resolve.
 */
const profileUrl = (accountId: string): string => `/wiki/people/${accountId}?ref=confluence`;

/** One person in the list, owning what pressing it does: report the press, then open the profile. */
const PersonListRow = ({ person }: { person: RankedPagePerson }): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: RELATED_PEOPLE_ROW_ACTION_SUBJECT_ID,
				source: ANALYTICS_SOURCE,
				// The relationship tells us whether readers open authors or mere viewers.
				// Deliberately no name or account id — this payload carries no personal data.
				attributes: { relationship: person.primaryRelationship },
			},
		}).fire();

		// Graph Store can return a person with no account id. Record the press, but skip
		// navigation rather than send the reader to a dead `/wiki/people/`.
		if (!person.accountId) {
			return;
		}
		window.open(profileUrl(person.accountId), '_blank', 'noopener,noreferrer');
	}, [createAnalyticsEvent, person]);

	return (
		<PersonRow
			// Hidden: `Avatar` labels itself with `name`, which the row already shows as text, so
			// without this the button announces the person twice. `name` stays for the initials.
			avatar={
				<span aria-hidden="true">
					<Avatar src={person.picture ?? undefined} name={person.name} size="medium" />
				</span>
			}
			name={person.name}
			role={formatMessage(messageForRelationship(person.primaryRelationship))}
			onClick={onClick}
			// Keyed on the ARI, the only id `parsePagePeople` guarantees is present.
			testId={`teamwork-graph-person-row-${person.ari}`}
		/>
	);
};

export type RelatedPeopleSectionProps = SectionExpansionProps & {
	/**
	 * Already ranked by `usePagePeople` and capped there to how many this section can list at
	 * most; this section does not re-rank. It applies only the collapsed cap below.
	 */
	people: readonly RankedPagePerson[];
	loading: boolean;
	/** True when discovery failed outright, so the section stays out of the way. */
	hasError: boolean;
};

/**
 * The people the Teamwork Graph connects to this page, the last section on both the panel and
 * the modal. Renders nothing while loading or on failure; an empty list keeps the heading with a
 * 0 badge and explains itself through `SectionEmptyState`, since the section is expected to be
 * there.
 *
 * Lists the three most relevant people and expands to the rest on a press. Expansion follows the
 * same controlled/uncontrolled split as `ConnectionsByLineSectionShell`, so the modal's
 * `useSectionExpansion` controller holds it across the swap to Rovo exactly as it does for the
 * sections above, and the panel — which passes no controller — owns it locally.
 */
export const RelatedPeopleSection = ({
	people,
	loading,
	hasError,
	isExpanded: controlledExpanded,
	onToggleExpanded,
}: RelatedPeopleSectionProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isExpanded, toggleExpanded] = useExpandedState(controlledExpanded, onToggleExpanded);

	const onToggle = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: RELATED_PEOPLE_SHOW_MORE_ACTION_SUBJECT_ID,
				source: ANALYTICS_SOURCE,
				// The direction of this press, not the state it was in — `true` is an expand.
				attributes: { isExpanding: !isExpanded },
			},
		}).fire();

		toggleExpanded();
	}, [createAnalyticsEvent, isExpanded, toggleExpanded]);

	// Nothing to show while discovery is in flight, and nothing to explain when it failed: an
	// error is not an empty graph, so the section stays out rather than claiming no one is here.
	if (loading || hasError) {
		return null;
	}

	const hasHiddenPeople = people.length > COLLAPSED_PAGE_PEOPLE;
	const listedPeople = isExpanded ? people : people.slice(0, COLLAPSED_PAGE_PEOPLE);

	return (
		<Stack space="space.100" testId="teamwork-graph-related-people">
			{/* The count is everyone the section can list, expanded or not, so it does not move
			    under the reader as they expand it. */}
			<SectionHeader
				icon={<PeopleGroupIcon label="" color="currentColor" />}
				label={formatMessage(i18n.heading)}
				count={people.length}
			/>
			{people.length === 0 ? (
				<SectionEmptyState
					message={formatMessage(i18n.empty)}
					testId="teamwork-graph-related-people-empty"
				/>
			) : (
				<PressableGroup testId="teamwork-graph-related-people-group">
					{listedPeople.map((person) => (
						<PersonListRow key={person.ari} person={person} />
					))}
					{hasHiddenPeople ? (
						<ShowMoreRow
							isExpanded={isExpanded}
							onClick={onToggle}
							testId="teamwork-graph-related-people-show-more"
						/>
					) : null}
				</PressableGroup>
			)}
		</Stack>
	);
};
